/**
 * Record that a session touched a document (RFC 0297 §4b).
 *
 * ONE IMPLEMENTATION, TWO CALLERS, and that is the point of the file. The
 * document tools record a read or a write; the window records an open, when a
 * document is on screen beside a conversation. Both have to read the document's
 * `updatedAt` at the moment of the act and both have to fail silently, and a
 * second copy of those two rules is how one of them ends up recording a touch
 * that says the wrong thing about what was seen.
 *
 * NEVER THROWS, and never blocks the caller. A touch is bookkeeping about
 * something that has already happened: a session log that cannot be reached is a
 * reason to hold nothing, not a reason to fail the read the agent asked for or
 * the pane the person opened.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import type { TouchKind } from '../../log/types'

async function ready(handle: {
  whenReady?: () => Promise<void>
}): Promise<void> {
  await handle.whenReady?.()
}

export async function recordTouch(
  repo: Repo,
  sessionId: string | undefined,
  documentId: string,
  kind: TouchKind,
): Promise<void> {
  if (!sessionId || !documentId) return
  try {
    const doc = await repo.find(documentId as AnyDocumentId)
    await ready(doc as { whenReady?: () => Promise<void> })
    // What this session ACTUALLY SAW, read after the act so a write records the
    // bytes it just made rather than the ones it replaced.
    const sawUpdatedAt = Number(
      (doc.doc() as { updatedAt?: unknown } | undefined)?.updatedAt ?? 0,
    )
    const session = await repo.find(sessionId as AnyDocumentId)
    await ready(session as { whenReady?: () => Promise<void> })
    new BranchLog(session as never).appendTouched({
      documentId,
      kind,
      sawUpdatedAt: Number.isFinite(sawUpdatedAt) ? sawUpdatedAt : 0,
    })
  } catch {
    // See above: holding nothing is the safe failure.
  }
}

/**
 * A document put on screen beside this session (§4b's `open` row).
 *
 * Named rather than left as `recordTouch(..., 'open')` at the call site, because
 * the window has no other reason to import a touch kind and the distinction that
 * matters to a reader there is that an open PINS.
 */
export function recordOpenTouch(
  repo: Repo,
  sessionId: string,
  documentId: string,
): Promise<void> {
  return recordTouch(repo, sessionId, documentId, 'open')
}
